import React from 'react';
import { graphql, ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import I18n from 'i18n-js';
import { get, isEqual } from 'lodash';

import SanitizedRender from '../../components/sanitized_render';
import ListingsCollection, { ListingsCollection as ListingsCollectionClass } from '../../components/listings_collection';

import {
  CommonsCmsRecentlyViewedListings,
  core_apimessages_Ribbon_Reason as IRibbonReason,
} from '../../rql';

import { formatExperimentsForEventAttributes } from '../../elog/mparticle';
import { MParticleEventName, trackEvent, CTA } from '../../elog/mparticle_tracker';

import CoreLink from '../../components/core_link';
import { IDynamicComponentProps } from '../dynamic_component_props';
import { IUserContext, withUserContext } from '../../components/user_context_provider';
import { isExperimentEnabled } from '../../user_context_helpers';
import experiments from '../../experiments';

interface ExternalProps extends IDynamicComponentProps {
  onSale?: boolean;
  ctaText?: string;
  ctaTargetHref?: string;
  largeTiles?: boolean;
  title?: string;
  maxCount?: number;
  titleHtml?: string;
  displayAsGrid?: boolean;
  jumplinkSlug?: string;
  loggedOutExperiment?: boolean;
  onVisible?: Function;
  displayAddToCart?: boolean;
  trackName?: string;
  experimentName?: string;
}

export type IProps = ExternalProps & IUserContext;

const COMPONENT_NAME = 'RecentlyViewedListings';
const DEFAULT_COUNT = 12;
const MIN_COUNT = 4;

function saleMaxCount(onSale: boolean, maxCount: number) {
  return (onSale ? 20 : maxCount);
}

export class RecentlyViewedListings extends React.Component<
ChildProps<IProps, CommonsCmsRecentlyViewedListings.Query, CommonsCmsRecentlyViewedListings.Variables>,
null
> {

  static get defaultProps(): Partial<IProps> {
    return {
      maxCount: DEFAULT_COUNT,
      loggedOutExperiment: false,
    };
  }

  componentDidMount() {
    this.logView();

    const { onVisible } = this.props;
    if (this.showListings() && onVisible) {
      onVisible();
    }
  }

  componentDidUpdate(oldProps) {
    const newListings = get(this.props, 'data.recentlyViewedListings', []);
    const oldListings = get(oldProps, 'data.recentlyViewedListings', []);
    if (!isEqual(newListings, oldListings)) {
      this.logView();
    }

    const { onVisible } = this.props;
    if (!isEqual(newListings, oldListings) && this.showListings() && onVisible) {
      onVisible();
    }
  }

  logView() {
    if (!this.hasEnoughListings()) return;

    const experimentTracking = !this.props.experimentName ? {} : {
      experiments: formatExperimentsForEventAttributes(this.props.user, [this.props.experimentName]),
    };

    const viewData = {
      componentName: this.props.trackName || COMPONENT_NAME,
      eventName: MParticleEventName.ComponentView,
      ...experimentTracking,
    };

    trackEvent(viewData);
  }

  hasEnoughListings() {
    return !this.loading() && this.listings().length >= MIN_COUNT;
  }

  renderCTA() {
    if (!this.props.ctaTargetHref) {
      return null;
    }

    return (
      <CoreLink
        className={this.props.displayAsGrid && 'button button--primary'}
        to={this.props.ctaTargetHref}
        interactionType={CTA}
      >
        {this.props.ctaText
          ? this.props.ctaText
          : I18n.t('cms.listingsCollection.seeMore')}
      </CoreLink>
    );
  }

  saleListings() {
    const listings = get(this.props, 'data.recentlyViewedListings');

    const salesListings = listings.filter((listing) => {
      const reason = get(listing, 'pricing.ribbon.reason', '');
      return reason === IRibbonReason.SALE;
    });

    return salesListings.slice(0, this.props.maxCount - 1);
  }

  loading() {
    return get(this.props, 'data.loading');
  }

  listings(): CommonsCmsRecentlyViewedListings.RecentlyViewedListings[] {
    const listings = get(this.props, 'data.recentlyViewedListings');

    if (this.props.onSale && listings) {
      return this.saleListings();
    }

    return listings || [];
  }

  renderTitle() {
    if (!this.props.title) return null;

    return (
      <SanitizedRender
        html={this.props.titleHtml || this.props.title}
      />
    );
  }

  showListings() {
    return this.hasEnoughListings();
  }

  render() {
    if (!this.showListings()) {
      return null;
    }

    if (this.props.experimentName && !isExperimentEnabled(this.props.user, this.props.experimentName)) {
      return null;
    }

    return (
      <ListingsCollection
        callToAction={this.renderCTA()}
        componentName={this.props.trackName || COMPONENT_NAME}
        displayAsGrid={this.props.displayAsGrid}
        largeTiles={this.props.largeTiles}
        listings={this.listings()}
        loading={this.loading()}
        maxCount={this.props.maxCount}
        title={this.renderTitle()}
        jumplinkSlug={this.props.jumplinkSlug}
        displayAddToCart={this.props.displayAddToCart}
      />
    );
  }
}

const connect = graphql<IProps, CommonsCmsRecentlyViewedListings.Query, CommonsCmsRecentlyViewedListings.Variables>(
  gql`
    query Commons_Cms_RecentlyViewedListings(
      $limit: Int
      $showListingLocation: Boolean!
    ) {
      recentlyViewedListings(input: {
        limit: $limit
      }) {
        _id
        ... ListingsCollection
        ... ShopFields @include(if: $showListingLocation)
      }
    }
    ${ListingsCollectionClass.fragments.listing}
    ${ListingsCollectionClass.fragments.shopFields}
  `,
  {
    options: ownProps => ({
      ssr: ownProps.ssr,
      variables: {
        limit: saleMaxCount(ownProps.onSale, ownProps.maxCount) || DEFAULT_COUNT,
        loggedOut: !!get(ownProps.user, 'loggedOut'),
        showListingLocation: isExperimentEnabled(ownProps.user, experiments.LISTING_CARD_LOCATION),
      },
    }),
  },
);

export default withUserContext<IProps>(connect(RecentlyViewedListings));
